import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Upload, Play, Pause, SkipBack, SkipForward, Scissors, Copy, ClipboardPaste,
  RotateCcw, Download, Save, Loader2, Volume2, Wand2
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

const fmtTime = (s) => {
  if (!isFinite(s)) return "0:00.0";
  const m = Math.floor(s / 60);
  const sec = (s - m * 60).toFixed(1);
  return `${m}:${sec.padStart(4, "0")}`;
};

// Lightweight WAV (PCM 16-bit) encoder for an AudioBuffer
function encodeWav(audioBuffer) {
  const numCh = audioBuffer.numberOfChannels;
  const sr = audioBuffer.sampleRate;
  const len = audioBuffer.length * numCh * 2 + 44;
  const buf = new ArrayBuffer(len);
  const view = new DataView(buf);
  const w = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  let off = 0;
  w(off, "RIFF"); off += 4;
  view.setUint32(off, len - 8, true); off += 4;
  w(off, "WAVE"); off += 4;
  w(off, "fmt "); off += 4;
  view.setUint32(off, 16, true); off += 4;
  view.setUint16(off, 1, true); off += 2;
  view.setUint16(off, numCh, true); off += 2;
  view.setUint32(off, sr, true); off += 4;
  view.setUint32(off, sr * numCh * 2, true); off += 4;
  view.setUint16(off, numCh * 2, true); off += 2;
  view.setUint16(off, 16, true); off += 2;
  w(off, "data"); off += 4;
  view.setUint32(off, audioBuffer.length * numCh * 2, true); off += 4;

  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(audioBuffer.getChannelData(c));
  for (let i = 0; i < audioBuffer.length; i++) {
    for (let c = 0; c < numCh; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      off += 2;
    }
  }
  return new Blob([buf], { type: "audio/wav" });
}

// Build a new AudioBuffer from one or more (buf, start, end) segments
function spliceBuffer(audioCtx, segments) {
  const numCh = segments[0].buf.numberOfChannels;
  const sr = segments[0].buf.sampleRate;
  let totalSamples = 0;
  segments.forEach(({ buf, start, end }) => {
    totalSamples += Math.floor((end - start) * sr);
  });
  const out = audioCtx.createBuffer(numCh, Math.max(1, totalSamples), sr);
  let offset = 0;
  segments.forEach(({ buf, start, end }) => {
    const startS = Math.floor(start * sr);
    const endS = Math.floor(end * sr);
    const segLen = endS - startS;
    for (let c = 0; c < numCh; c++) {
      const src = buf.getChannelData(Math.min(c, buf.numberOfChannels - 1));
      const dst = out.getChannelData(c);
      for (let i = 0; i < segLen; i++) dst[offset + i] = src[startS + i];
    }
    offset += segLen;
  });
  return out;
}

export default function AudioTrimmer() {
  const [audioBuf, setAudioBuf] = useState(null);
  const [originalName, setOriginalName] = useState("voice-over");
  const [saveKind, setSaveKind] = useState("voice");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [clipboard, setClipboard] = useState(null); // {buf, start, end}
  const [busy, setBusy] = useState(false);
  const [normalize, setNormalize] = useState(false);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const startedAtRef = useRef(0);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);

  const getCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  };

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !audioBuf) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth, cssH = canvas.clientHeight;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, cssH);

    // Background
    ctx.fillStyle = "#060608";
    ctx.fillRect(0, 0, cssW, cssH);
    // Trimmed (active) region highlight
    const dur = audioBuf.duration;
    const sx = (trimStart / dur) * cssW;
    const ex = (trimEnd / dur) * cssW;
    ctx.fillStyle = "rgba(255,149,0,0.08)";
    ctx.fillRect(sx, 0, ex - sx, cssH);
    // Midline
    ctx.strokeStyle = "#272730";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, cssH / 2);
    ctx.lineTo(cssW, cssH / 2);
    ctx.stroke();

    // Mono mixdown for visualization
    const data = audioBuf.getChannelData(0);
    const step = Math.ceil(data.length / cssW);
    const amp = cssH / 2;
    for (let x = 0; x < cssW; x++) {
      let min = 1, max = -1;
      const base = x * step;
      for (let i = 0; i < step; i++) {
        const v = data[base + i] || 0;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      const inTrim = x >= sx && x <= ex;
      ctx.fillStyle = inTrim ? "#0093FF" : "#3a3a44";
      ctx.fillRect(x, (1 - max) * amp, 1, Math.max(1, (max - min) * amp));
    }

    // Trim handles
    ctx.fillStyle = "#0093FF";
    ctx.fillRect(sx - 1, 0, 2, cssH);
    ctx.fillRect(ex - 1, 0, 2, cssH);
    ctx.fillStyle = "rgba(255,149,0,0.9)";
    ctx.beginPath(); ctx.arc(sx, 10, 6, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(ex, cssH - 10, 6, 0, Math.PI * 2); ctx.fill();

    // Playhead
    if (playing || playhead > 0) {
      const px = (playhead / dur) * cssW;
      ctx.strokeStyle = "#FF3B30";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(px, 0); ctx.lineTo(px, cssH);
      ctx.stroke();
    }
  }, [audioBuf, trimStart, trimEnd, playhead, playing]);

  useEffect(() => { drawWaveform(); }, [drawWaveform]);
  useEffect(() => {
    const r = () => drawWaveform();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, [drawWaveform]);

  const loadFile = async (file) => {
    if (!file) return;
    if (file.size > 250 * 1024 * 1024) { toast.error("File > 250 MB"); return; }
    setBusy(true);
    setOriginalName(file.name.replace(/\.[^.]+$/, ""));
    try {
      const arr = await file.arrayBuffer();
      const ctx = getCtx();
      const decoded = await ctx.decodeAudioData(arr.slice(0));
      setAudioBuf(decoded);
      setTrimStart(0);
      setTrimEnd(decoded.duration);
      setPlayhead(0);
      toast.success(`Loaded ${decoded.duration.toFixed(1)}s`);
    } catch (e) {
      toast.error("Could not decode audio");
    } finally { setBusy(false); }
  };

  const stop = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch {}
      sourceRef.current = null;
    }
    cancelAnimationFrame(rafRef.current);
    setPlaying(false);
  }, []);

  const playTrim = () => {
    if (!audioBuf) return;
    stop();
    const ctx = getCtx();
    const src = ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(ctx.destination);
    const len = Math.max(0.05, trimEnd - trimStart);
    src.start(0, trimStart, len);
    sourceRef.current = src;
    startedAtRef.current = ctx.currentTime;
    offsetRef.current = trimStart;
    setPlaying(true);
    const tick = () => {
      const elapsed = ctx.currentTime - startedAtRef.current;
      const pos = offsetRef.current + elapsed;
      if (pos >= trimEnd) { stop(); setPlayhead(trimEnd); return; }
      setPlayhead(pos);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    src.onended = () => { stop(); };
  };

  // Drag trim handles
  const onCanvasMouseDown = (e) => {
    if (!audioBuf) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const dur = audioBuf.duration;
    const sx = (trimStart / dur) * rect.width;
    const ex = (trimEnd / dur) * rect.width;
    const which = Math.abs(x - sx) < Math.abs(x - ex) ? "start" : "end";
    const move = (ev) => {
      const nx = Math.max(0, Math.min(rect.width, ev.clientX - rect.left));
      const t = (nx / rect.width) * dur;
      if (which === "start") setTrimStart(Math.min(t, trimEnd - 0.05));
      else setTrimEnd(Math.max(t, trimStart + 0.05));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // Edit ops
  const doCopy = () => {
    if (!audioBuf) return;
    setClipboard({ buf: audioBuf, start: trimStart, end: trimEnd });
    toast.success(`Copied ${(trimEnd - trimStart).toFixed(2)}s`);
  };
  const doCut = () => {
    if (!audioBuf) return;
    setClipboard({ buf: audioBuf, start: trimStart, end: trimEnd });
    const ctx = getCtx();
    const segs = [];
    if (trimStart > 0.001) segs.push({ buf: audioBuf, start: 0, end: trimStart });
    if (trimEnd < audioBuf.duration - 0.001) segs.push({ buf: audioBuf, start: trimEnd, end: audioBuf.duration });
    if (segs.length === 0) { toast.error("Can't cut entire clip"); return; }
    const out = spliceBuffer(ctx, segs);
    setAudioBuf(out);
    setTrimStart(0);
    setTrimEnd(out.duration);
    setPlayhead(0);
    toast.success("Cut selection");
  };
  const doPaste = () => {
    if (!audioBuf || !clipboard) { toast.error("Nothing in clipboard"); return; }
    const ctx = getCtx();
    const insertAt = trimStart;
    const segs = [];
    if (insertAt > 0.001) segs.push({ buf: audioBuf, start: 0, end: insertAt });
    segs.push({ buf: clipboard.buf, start: clipboard.start, end: clipboard.end });
    if (insertAt < audioBuf.duration) segs.push({ buf: audioBuf, start: insertAt, end: audioBuf.duration });
    const out = spliceBuffer(ctx, segs);
    setAudioBuf(out);
    setTrimEnd(out.duration);
    setPlayhead(0);
    toast.success(`Pasted ${(clipboard.end - clipboard.start).toFixed(2)}s`);
  };
  const reset = () => {
    if (!audioBuf) return;
    setTrimStart(0);
    setTrimEnd(audioBuf.duration);
    setPlayhead(0);
    toast.success("Trim reset");
  };

  // AI Auto-Trim: detect silence at head/tail by adaptive RMS threshold
  const autoTrim = () => {
    if (!audioBuf) return;
    const sr = audioBuf.sampleRate;
    const winMs = 20;
    const winSamples = Math.max(64, Math.floor((winMs / 1000) * sr));
    const numCh = audioBuf.numberOfChannels;
    const channels = [];
    for (let c = 0; c < numCh; c++) channels.push(audioBuf.getChannelData(c));
    const len = audioBuf.length;
    const numWin = Math.floor(len / winSamples);
    if (numWin < 4) { toast.error("Clip too short to auto-trim"); return; }

    // RMS per window (mono mixdown)
    const rms = new Float32Array(numWin);
    for (let w = 0; w < numWin; w++) {
      let sum = 0;
      const base = w * winSamples;
      for (let i = 0; i < winSamples; i++) {
        let s = 0;
        for (let c = 0; c < numCh; c++) s += channels[c][base + i] || 0;
        s = s / numCh;
        sum += s * s;
      }
      rms[w] = Math.sqrt(sum / winSamples);
    }

    // Noise floor = median of quietest 15% windows
    const sorted = Array.from(rms).sort((a, b) => a - b);
    const noiseFloor = sorted[Math.floor(sorted.length * 0.15)] || 0.0001;
    // Threshold: 3.5× noise floor OR absolute -42 dBFS (~0.0079), whichever is higher
    const absMin = 0.0079;
    const threshold = Math.max(noiseFloor * 3.5, absMin);

    let firstHot = -1, lastHot = -1;
    for (let w = 0; w < numWin; w++) {
      if (rms[w] > threshold) { firstHot = w; break; }
    }
    for (let w = numWin - 1; w >= 0; w--) {
      if (rms[w] > threshold) { lastHot = w; break; }
    }
    if (firstHot === -1 || lastHot === -1 || lastHot <= firstHot) {
      toast.error("No speech detected — leaving trim unchanged");
      return;
    }
    // Add ~80ms lead-in / lag-out so first syllable isn't clipped
    const padWindows = Math.ceil(80 / winMs);
    const startWin = Math.max(0, firstHot - padWindows);
    const endWin = Math.min(numWin - 1, lastHot + padWindows);
    const newStart = (startWin * winSamples) / sr;
    const newEnd = ((endWin + 1) * winSamples) / sr;
    setTrimStart(newStart);
    setTrimEnd(Math.min(audioBuf.duration, newEnd));
    setPlayhead(newStart);
    const removed = (audioBuf.duration - (newEnd - newStart)).toFixed(2);
    toast.success(`Auto-trimmed · removed ${removed}s of silence`);
  };

  const buildTrimmedBuffer = () => {
    if (!audioBuf) return null;
    const ctx = getCtx();
    return spliceBuffer(ctx, [{ buf: audioBuf, start: trimStart, end: trimEnd }]);
  };

  const normalizeBuffer = (buf) => {
    let peak = 0;
    for (let c = 0; c < buf.numberOfChannels; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < d.length; i++) {
        const v = Math.abs(d[i]);
        if (v > peak) peak = v;
      }
    }
    if (peak === 0 || peak >= 0.999) return buf;
    const gain = 0.99 / peak;
    for (let c = 0; c < buf.numberOfChannels; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < d.length; i++) d[i] = d[i] * gain;
    }
    return buf;
  };

  const downloadFile = () => {
    let trimmed = buildTrimmedBuffer();
    if (!trimmed) return;
    if (normalize) trimmed = normalizeBuffer(trimmed);
    const blob = encodeWav(trimmed);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${originalName}-trim.wav`;
    a.rel = "noopener";
    // Anchor must be in the DOM for click() to fire a download cross-browser.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    toast.success("Download started");
  };

  const saveToLibrary = async () => {
    let trimmed = buildTrimmedBuffer();
    if (!trimmed) return;
    if (normalize) trimmed = normalizeBuffer(trimmed);
    setBusy(true);
    try {
      const blob = encodeWav(trimmed);
      const fd = new FormData();
      const file = new File([blob], `${originalName}-trim.wav`, { type: "audio/wav" });
      fd.append("file", file);
      fd.append("kind", saveKind);
      const kindLabel = { voice: "voice", bed: "bed", fx: "fx", opener: "opener", closer: "closer" }[saveKind] || saveKind;
      fd.append("label", `${originalName} · ${kindLabel} (trimmed ${trimmed.duration.toFixed(1)}s)`);
      const res = await fetch(`${API_BASE}/imaging/assets/upload`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) throw new Error("upload failed");
      toast.success(`Saved to ${kindLabel} library`);
      window.dispatchEvent(new CustomEvent("imaging-assets-refresh"));
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const trimDuration = trimEnd - trimStart;
  const totalDuration = audioBuf?.duration || 0;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="trim-edit-tab">
      <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-6">
        <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-3">// STEP 1 · TRIM & EDIT AUDIO (voice, beds, stingers, FX)</div>

        {!audioBuf ? (
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); loadFile(e.dataTransfer.files?.[0]); }}
            className="rounded-xl border-2 border-dashed border-line hover:border-brand-amber/60 transition cursor-pointer p-12 text-center"
            data-testid="trim-dropzone"
          >
            <Upload className="h-10 w-10 mx-auto text-brand-amber mb-4" />
            <div className="font-display text-xl font-bold mb-1">Upload audio to trim</div>
            <div className="text-xs text-ink-muted mb-2">Voice-overs · music beds · stingers · FX · podcast cuts</div>
            <div className="text-xs text-ink-muted font-mono">MP3 · WAV · M4A · AAC · FLAC · OGG · up to 250 MB</div>
            <input ref={fileRef} type="file" accept=".mp3,.wav,.m4a,.aac,.flac,.ogg,audio/*" className="hidden" onChange={(e) => loadFile(e.target.files?.[0])} data-testid="trim-file-input" />
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-line bg-bg p-3 mb-4">
              <canvas
                ref={canvasRef}
                onMouseDown={onCanvasMouseDown}
                className="w-full h-32 cursor-ew-resize"
                style={{ display: "block" }}
                data-testid="trim-canvas"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono mb-4" data-testid="trim-timecodes">
              <span className="rounded-md border border-line bg-bg-elevated px-2 py-1">START <span className="text-brand-amber">{fmtTime(trimStart)}</span></span>
              <span className="rounded-md border border-line bg-bg-elevated px-2 py-1">END <span className="text-brand-amber">{fmtTime(trimEnd)}</span></span>
              <span className="rounded-md border border-brand-amber/40 bg-brand-amber/10 px-2 py-1 text-brand-amber">DURATION {fmtTime(trimDuration)}</span>
              <span className="rounded-md border border-line bg-bg-elevated px-2 py-1 text-ink-muted">FULL {fmtTime(totalDuration)}</span>
              <span className="ml-auto rounded-md border border-line bg-bg-elevated px-2 py-1">PLAY <span className="text-brand-onair">{fmtTime(playhead)}</span></span>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              <button onClick={playing ? stop : playTrim} data-testid="trim-play" className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow">
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                {playing ? "Pause" : "Play trim"}
              </button>
              <button onClick={() => { stop(); setPlayhead(trimStart); }} data-testid="trim-skip-start" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-cyan-300 transition">
                <SkipBack className="h-3.5 w-3.5" /> Skip to start
              </button>
              <button onClick={() => { stop(); setPlayhead(trimEnd); }} data-testid="trim-skip-end" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-cyan-300 transition">
                <SkipForward className="h-3.5 w-3.5" /> Skip to end
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={autoTrim} data-testid="trim-auto" className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber/15 border border-brand-amber/50 text-brand-amber px-3 py-2 text-xs font-semibold hover:bg-brand-amber/25 transition">
                <Wand2 className="h-3.5 w-3.5" /> AI Auto-Trim
              </button>
              <button onClick={doCopy} data-testid="trim-copy" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-cyan-300 transition">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button onClick={doCut} data-testid="trim-cut" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-cyan-300 transition">
                <Scissors className="h-3.5 w-3.5" /> Cut
              </button>
              <button onClick={doPaste} disabled={!clipboard} data-testid="trim-paste" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-cyan-300 transition disabled:opacity-40">
                <ClipboardPaste className="h-3.5 w-3.5" /> Paste {clipboard ? <span className="text-ink-muted">({(clipboard.end - clipboard.start).toFixed(1)}s)</span> : null}
              </button>
              <button onClick={reset} data-testid="trim-reset" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-cyan-300 transition">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </button>
              <button onClick={() => { stop(); setAudioBuf(null); }} data-testid="trim-clear" className="ml-auto rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-brand-onair transition">
                Load different file
              </button>
            </div>
          </>
        )}
        {busy && <div className="mt-3 text-xs text-brand-amber font-mono animate-pulse">PROCESSING...</div>}
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="trim-export">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-3">// EXPORT</div>
          <div className="mb-3">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Save as</Label>
            <Select value={saveKind} onValueChange={setSaveKind}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="trim-save-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                <SelectItem value="voice">Voice take</SelectItem>
                <SelectItem value="bed">Music bed</SelectItem>
                <SelectItem value="opener">Opener stinger</SelectItem>
                <SelectItem value="closer">Closer stinger</SelectItem>
                <SelectItem value="fx">Sound FX</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[10px] text-ink-muted mt-1 font-mono">
              {saveKind === "bed" && "Tip: clip a 3-min song to an 8-15s loop for sweepers."}
              {saveKind === "voice" && "Trimmed voice-overs go to the Voice dropdown in the Mixer."}
              {saveKind === "opener" && "Short branding stingers used at the start of imaging."}
              {saveKind === "closer" && "Short branding stingers used at the end of imaging."}
              {saveKind === "fx" && "One-shot sound effects — whooshes, hits, impacts."}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-3 mb-3">
            <div>
              <div className="text-xs font-medium">Normalize on export</div>
              <div className="text-[10px] text-ink-muted">Peak-normalize to 0 dBFS.</div>
            </div>
            <Switch checked={normalize} onCheckedChange={setNormalize} data-testid="trim-normalize" />
          </div>
          <button onClick={downloadFile} disabled={!audioBuf || busy} data-testid="trim-download" className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm font-semibold hover:text-cyan-300 transition disabled:opacity-50 mb-2">
            <Download className="h-4 w-4" /> Download file (WAV)
          </button>
          <button onClick={saveToLibrary} disabled={!audioBuf || busy} data-testid="trim-save-library" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save to library
          </button>
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// HINTS</div>
          <ul className="text-xs text-ink-secondary space-y-1.5">
            <li>· <span className="text-brand-amber">AI Auto-Trim</span> snaps handles to the first/last loud sample — perfect for cleaning sloppy phone recordings.</li>
            <li>· Drag the <span className="text-brand-amber">amber handles</span> on the waveform to set trim points.</li>
            <li>· <span className="text-brand-amber">Cut</span> removes the selected region. <span className="text-brand-amber">Copy</span> stores it.</li>
            <li>· <span className="text-brand-amber">Paste</span> inserts at the current start handle position.</li>
            <li>· "Save to library" sends the trimmed clip to your Voice takes for the Mixer.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
